import React, { useState, useEffect } from 'react';
import { enhancedApi } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  UserPlus, 
  UserMinus,
  Search,
  CheckCircle2,
  XCircle,
  Shield,
  Crown
} from 'lucide-react';

interface Team {
  id: number;
  name: string;
  description: string;
  lead_auditor_id: number;
  lead_auditor_name: string;
  member_count: number;
  is_active: boolean;
  created_at: string;
}

interface TeamMember {
  id: number;
  team_id: number;
  user_id: number;
  user_name: string;
  role: 'lead_auditor' | 'auditor' | 'reviewer';
  joined_at: string;
}

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  department_name: string;
  unit_name: string;
  is_active: boolean;
}

export const TeamManagement: React.FC = () => {
  const { addToast } = useToast();
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showManageMembersModal, setShowManageMembersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState<'team' | 'member'>('team');
  const [itemToDelete, setItemToDelete] = useState<Team | TeamMember | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  // Team form state
  const [teamForm, setTeamForm] = useState({
    name: '',
    description: '',
    lead_auditor_id: ''
  });

  // Member form state
  const [memberForm, setMemberForm] = useState({
    user_id: '',
    role: 'auditor' as 'lead_auditor' | 'auditor' | 'reviewer'
  });

  useEffect(() => {
    fetchTeams();
    fetchUsers();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await enhancedApi.teams.getTeams();
      if (response.success && 'data' in response) {
        setTeams(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await enhancedApi.teams.getAvailableUsers();
      if (response.success && 'data' in response) {
        setAvailableUsers(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setAvailableUsers([]);
    }
  };

  const fetchTeamMembers = async (teamId: number) => {
    try {
      const response = await enhancedApi.teams.getTeamMembers(teamId);
      if (response.success && 'data' in response) {
        setTeamMembers(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      setTeamMembers([]);
    }
  };

  const handleAddTeam = () => {
    setCurrentTeam(null);
    setIsEditing(false);
    setTeamForm({ name: '', description: '', lead_auditor_id: '' });
    setFormErrors([]);
    setShowTeamModal(true);
  };

  const handleEditTeam = (team: Team) => {
    setCurrentTeam(team);
    setIsEditing(true);
    setTeamForm({
      name: team.name,
      description: team.description,
      lead_auditor_id: team.lead_auditor_id.toString()
    });
    setFormErrors([]);
    setShowTeamModal(true);
  };

  const handleDeleteTeam = (team: Team) => {
    setItemToDelete(team);
    setDeleteType('team');
    setShowDeleteModal(true);
  };

  const handleAddMember = (teamId: number) => {
    setSelectedTeam(teamId);
    setMemberForm({ user_id: '', role: 'auditor' });
    setFormErrors([]);
    setShowMemberModal(true);
  };

  const handleManageMembers = async (teamId: number) => {
    setSelectedTeam(teamId);
    await fetchTeamMembers(teamId);
    setShowManageMembersModal(true);
  };

  const handleRemoveMember = (member: TeamMember) => {
    setItemToDelete(member);
    setDeleteType('member');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      if (deleteType === 'team') {
        const team = itemToDelete as Team;
        const response = await enhancedApi.teams.deleteTeam(team.id);
        if (response.success) {
          setTeams(prev => prev.filter(t => t.id !== team.id));
        } else {
          addToast({
            type: 'error',
            title: 'Delete Failed',
            message: `Failed to delete team: ${response.message}`
          });
          return;
        }
      } else {
        const member = itemToDelete as TeamMember;
        const response = await enhancedApi.teams.removeTeamMember(member.id);
        if (response.success) {
          // Update local state
          setTeamMembers(prev => prev.filter(m => m.id !== member.id));
          
          // Update team member count
          setTeams(prev => prev.map(team => 
            team.id === selectedTeam 
              ? { ...team, member_count: team.member_count - 1 }
              : team
          ));
          addToast({
            type: 'success',
            title: 'Member Removed',
            message: 'Member has been successfully removed from the team'
          });
        } else {
          addToast({
            type: 'error',
            title: 'Remove Failed',
            message: `Failed to remove member: ${response.message}`
          });
          return;
        }
      }
      
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error(`Failed to delete ${deleteType}:`, error);
      addToast({
        type: 'error',
        title: 'Delete Failed',
        message: `Failed to delete ${deleteType}`
      });
    }
  };

  const handleTeamSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors([]);

    if (!teamForm.name.trim()) {
      setFormErrors(['Team name is required']);
      return;
    }

    if (!teamForm.lead_auditor_id) {
      setFormErrors(['Lead auditor is required']);
      return;
    }

    try {
      const teamData = {
        name: teamForm.name,
        description: teamForm.description,
        lead_auditor_id: parseInt(teamForm.lead_auditor_id)
      };

      if (isEditing && currentTeam) {
        // Update existing team
        const response = await enhancedApi.teams.updateTeam(currentTeam.id, teamData);
        if (response.success) {
          fetchTeams(); // Refresh teams list
          addToast({
            type: 'success',
            title: 'Team Updated',
            message: 'Team updated successfully'
          });
          setShowTeamModal(false);
        } else {
          addToast({
            type: 'error',
            title: 'Update Failed',
            message: `Failed to update team: ${response.message}`
          });
        }
      } else {
        // Create new team
        const response = await enhancedApi.teams.createTeam(teamData);
        if (response.success) {
          fetchTeams(); // Refresh teams list
          addToast({
            type: 'success',
            title: 'Team Created',
            message: 'Team created successfully'
          });
          setShowTeamModal(false);
        } else {
          addToast({
            type: 'error',
            title: 'Creation Failed',
            message: `Failed to create team: ${response.message}`
          });
        }
      }
    } catch (error) {
      console.error('Failed to save team:', error);
      addToast({
        type: 'error',
        title: 'Save Failed',
        message: 'Failed to save team'
      });
    }
  };

  const handleMemberSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors([]);

    if (!memberForm.user_id) {
      setFormErrors(['User selection is required']);
      return;
    }

    if (!selectedTeam) {
      setFormErrors(['Team selection is required']);
      return;
    }

    try {
      const memberData = {
        team_id: selectedTeam,
        user_id: parseInt(memberForm.user_id),
        role: memberForm.role
      };

      const response = await enhancedApi.teams.addTeamMember(memberData);
      if (response.success) {
        // Refresh team members and teams list
        await fetchTeamMembers(selectedTeam);
        await fetchTeams();
        addToast({
          type: 'success',
          title: 'Member Added',
          message: 'Member added successfully'
        });
        setShowMemberModal(false);
      } else {
        addToast({
          type: 'error',
          title: 'Add Failed',
          message: `Failed to add member: ${response.message}`
        });
      }
    } catch (error) {
      console.error('Failed to add member:', error);
      addToast({
        type: 'error',
        title: 'Add Failed',
        message: 'Failed to add member'
      });
    }
  };

  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.lead_auditor_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'lead_auditor':
        return <Crown className="h-4 w-4 text-yellow-600" />;
      case 'auditor':
        return <Shield className="h-4 w-4 text-blue-600" />;
      case 'reviewer':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      default:
        return <Users className="h-4 w-4 text-gray-600" />;
    }
  };

  const getRoleBadge = (role: string) => {
    const roleClasses = {
      lead_auditor: 'bg-yellow-100 text-yellow-800',
      auditor: 'bg-blue-100 text-blue-800',
      reviewer: 'bg-green-100 text-green-800'
    };
    return roleClasses[role as keyof typeof roleClasses] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 px-8 py-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
                <p className="text-gray-600">Create and manage audit teams</p>
              </div>
            </div>
            <button
              onClick={handleAddTeam}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl transition-all duration-200 flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Create Team</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search and Stats */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search teams..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{teams.length}</div>
              <div className="text-sm text-gray-600">Total Teams</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {teams.reduce((sum, team) => sum + team.member_count, 0)}
              </div>
              <div className="text-sm text-gray-600">Total Members</div>
            </div>
          </div>
        </div>
      </div>

      {/* Teams Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-600">Loading teams...</span>
          </div>
        </div>
      ) : filteredTeams.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No teams found</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredTeams.map((team) => (
            <div key={team.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{team.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{team.description}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditTeam(team)}
                    className="p-2 text-gray-400 hover:text-blue-600 rounded-lg transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteTeam(team)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Lead Auditor:</span>
                  <span className="text-sm font-medium text-gray-900">{team.lead_auditor_name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Members:</span>
                  <span className="text-sm font-medium text-gray-900">{team.member_count}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status:</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    team.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {team.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                <button
                  onClick={() => handleManageMembers(team.id)}
                  className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <Users className="h-4 w-4" />
                  <span>Manage Members</span>
                </button>
                <button
                  onClick={() => handleAddMember(team.id)}
                  className="w-full bg-green-50 hover:bg-green-100 text-green-700 px-3 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <UserPlus className="h-4 w-4" />
                  <span>Add Member</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Team Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Edit Team' : 'Create New Team'}
              </h2>
              <button
                onClick={() => setShowTeamModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleTeamSubmit} className="p-6 space-y-4">
              {formErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <ul className="text-sm text-red-600">
                    {formErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Name *
                </label>
                <input
                  type="text"
                  value={teamForm.name}
                  onChange={(e) => setTeamForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter team name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={teamForm.description}
                  onChange={(e) => setTeamForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Enter team description"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Auditor *
                </label>
                <select
                  value={teamForm.lead_auditor_id}
                  onChange={(e) => setTeamForm(prev => ({ ...prev, lead_auditor_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select lead auditor...</option>
                  {availableUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} - {user.department_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowTeamModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  {isEditing ? 'Update Team' : 'Create Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Member Modal */}
      {showMemberModal && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Add Team Member</h2>
              <button
                onClick={() => setShowMemberModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleMemberSubmit} className="p-6 space-y-4">
              {formErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <ul className="text-sm text-red-600">
                    {formErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select User *
                </label>
                <select
                  value={memberForm.user_id}
                  onChange={(e) => setMemberForm(prev => ({ ...prev, user_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select user...</option>
                  {availableUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} - {user.department_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role *
                </label>
                <select
                  value={memberForm.role}
                  onChange={(e) => setMemberForm(prev => ({ ...prev, role: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="auditor">Auditor</option>
                  <option value="reviewer">Reviewer</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowMemberModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Add Member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Members Modal */}
      {showManageMembersModal && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Manage Team Members</h2>
              <button
                onClick={() => setShowManageMembersModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6">
              {teamMembers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No members in this team</p>
                  <button
                    onClick={() => {
                      setShowManageMembersModal(false);
                      handleAddMember(selectedTeam);
                    }}
                    className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Add First Member
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">
                      Team Members ({teamMembers.length})
                    </h3>
                    <button
                      onClick={() => {
                        setShowManageMembersModal(false);
                        handleAddMember(selectedTeam);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <UserPlus className="h-4 w-4" />
                      <span>Add Member</span>
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {teamMembers.map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            {member.role === 'lead_auditor' ? (
                              <Crown className="h-5 w-5 text-blue-600" />
                            ) : member.role === 'auditor' ? (
                              <Shield className="h-5 w-5 text-blue-600" />
                            ) : (
                              <Users className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{member.user_name}</p>
                            <p className="text-sm text-gray-600 capitalize">
                              {member.role.replace('_', ' ')}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {member.role === 'lead_auditor' && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                              Lead
                            </span>
                          )}
                          <button
                            onClick={() => handleRemoveMember(member)}
                            className="p-2 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
                            title="Remove member"
                          >
                            <UserMinus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {deleteType === 'team' ? 'Delete Team' : 'Remove Member'}
                </h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                {deleteType === 'team' 
                  ? 'Are you sure you want to permanently delete this team?'
                  : 'Are you sure you want to remove this member from the team?'
                }
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm">
                  {deleteType === 'team' ? (
                    <>
                      <div className="mb-2">
                        <span className="font-medium text-gray-700">Team Name:</span>
                        <p className="text-gray-900">{(itemToDelete as Team).name}</p>
                      </div>
                      <div className="mb-2">
                        <span className="font-medium text-gray-700">Description:</span>
                        <p className="text-gray-900">{(itemToDelete as Team).description}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Members:</span>
                        <p className="text-gray-900">{(itemToDelete as Team).member_count}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-2">
                        <span className="font-medium text-gray-700">Member Name:</span>
                        <p className="text-gray-900">{(itemToDelete as TeamMember).user_name}</p>
                      </div>
                      <div className="mb-2">
                        <span className="font-medium text-gray-700">Role:</span>
                        <p className="text-gray-900 capitalize">{(itemToDelete as TeamMember).role}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">User ID:</span>
                        <p className="text-gray-900">{(itemToDelete as TeamMember).user_id}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <p className="text-sm text-red-600 mt-3">
                ⚠️ {deleteType === 'team' 
                  ? 'This will permanently remove the team and all associated data.'
                  : 'This will remove the member from the team.'
                }
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => {
                  setShowDeleteModal(false);
                  setItemToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-2 inline" />
                {deleteType === 'team' ? 'Delete Team' : 'Remove Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
